"use client";

import Script from "next/script";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import {
  GA_MEASUREMENT_ID,
  GTM_ID,
  pageview,
} from "@/lib/analytics";

/**
 * Loads Google Tag Manager when NEXT_PUBLIC_GTM_ID is set. Falls back
 * to direct gtag.js loading using NEXT_PUBLIC_GA_MEASUREMENT_ID so the
 * site keeps tracking until the GTM container is configured.
 *
 * Pageviews are pushed via usePathname:
 *   - GTM mode: dataLayer push { event: 'pageview', page_path }
 *   - GA fallback: gtag('config', ID, { page_path })
 */
export default function GoogleTagManager() {
  const pathname = usePathname();

  useEffect(() => {
    if (pathname) pageview(pathname);
  }, [pathname]);

  if (GTM_ID) {
    return (
      <Script
        id="gtm-init"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${GTM_ID}');
          `.trim(),
        }}
      />
    );
  }

  if (GA_MEASUREMENT_ID) {
    return (
      <>
        <Script
          strategy="afterInteractive"
          src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
        />
        <Script
          id="gtag-init"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${GA_MEASUREMENT_ID}', { page_path: window.location.pathname });
            `,
          }}
        />
      </>
    );
  }

  return null;
}

/**
 * <noscript> iframe fallback for GTM. Render this near the top of
 * <body> so users without JS still fire the container.
 */
export function GoogleTagManagerNoScript() {
  if (!GTM_ID) return null;
  return (
    <noscript>
      <iframe
        src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
        height="0"
        width="0"
        style={{ display: "none", visibility: "hidden" }}
      />
    </noscript>
  );
}
